import React, { ReactElement, useState } from "react";
import { useDropzone } from "react-dropzone";
import cx from "classnames";

import "./styles.css";
import {
  CornerDownRight,
  Folder,
  MoreVertical,
  Plus,
  Trash2,
  Settings,
} from "react-feather";
import { handleError } from "../../../utils/handleError";
import {
  uploadFile,
  uploadManualFiles,
  deleteSelectedFiles,
} from "../../../api/files";
import {
  AddFiles,
  UpdateFile,
  SelectedFile,
} from "../../../types/PostSelection";
import { FlexAlign } from "../../../components/_ui/FlexAlign";
import { ConfirmationModal } from "../../../components/_ui/ConfirmationModal";
import { ManualUploadModal } from "../../../components/_ui/ManualUploadModal";
import toast from "react-hot-toast";

export function FileUpload({
  children,
  addFiles,
  updateFile,
  isEmpty,
  currentFolder = "",
  setShowCreateFolder,
  selectedCount,
  setShowFolderSelectModal,
  selectedFiles = [],
  onDeleteComplete,
}: {
  children: ReactElement;
  addFiles: AddFiles;
  updateFile: UpdateFile;
  isEmpty: boolean;
  currentFolder?: string;
  setShowCreateFolder: React.Dispatch<React.SetStateAction<boolean>>;
  selectedCount?: number;
  setShowFolderSelectModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedFiles?: SelectedFile[];
  onDeleteComplete?: () => void;
}) {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showManualUpload, setShowManualUpload] = useState(false);

  const handleDeleteSelected = async () => {
    try {
      // Filter selected files that are not used
      const unusedSelectedFiles = selectedFiles.filter((file) => !file.isUsed);

      if (unusedSelectedFiles.length === 0) {
        toast.error(
          "All selected files are being used in posts and cannot be deleted"
        );
        return;
      }

      const deletedCount = await deleteSelectedFiles(
        unusedSelectedFiles,
        currentFolder
      );

      if (deletedCount > 0) {
        toast.success(
          `Deleted ${deletedCount} file${deletedCount > 1 ? "s" : ""}`
        );

        // Clear selections for deleted files
        unusedSelectedFiles.forEach((file) => {
          updateFile(file.name, { isSelected: false });
        });

        // Trigger refresh if callback provided
        onDeleteComplete?.();
      } else {
        toast.error("Failed to delete files");
      }
    } catch (error) {
      handleError(error as Error);
    } finally {
      setShowDeleteConfirmation(false);
    }
  };

  const confirmDelete = () => {
    // Check if any selected files are not used
    const unusedSelectedFiles = selectedFiles.filter((file) => !file.isUsed);

    if (unusedSelectedFiles.length === 0) {
      toast.error(
        "All selected files are being used in posts and cannot be deleted"
      );
      return;
    }

    setShowDeleteConfirmation(true);
  };

  const handleManualUpload = async (
    files: SelectedFile[],
    actualFiles?: {
      thumbnail: File[];
      small: File[];
      medium: File[];
      large: File[];
      original: File[];
    }
  ) => {
    if (actualFiles) {
      try {
        // Use the manual upload function with pre-sized files
        const uploadedFile = await uploadManualFiles(
          actualFiles,
          currentFolder
        );

        // Add only ONE file to represent the multi-size upload set
        // Use the thumbnail file name as the representative name
        const representativeFile: SelectedFile = {
          id: Date.now(),
          name: uploadedFile.name,
          mine_type: uploadedFile.mine_type,
          url: uploadedFile.url || "",
          rating: uploadedFile.rating,
          imageDerivatives: uploadedFile.imageDerivatives,
          isUploading: false,
        };

        // Add the single representative file to the UI
        addFiles([representativeFile]);
      } catch (err) {
        handleError(err as Error);
        toast.error("Failed to upload manual files");
      }
    } else {
      // Fallback for individual files (shouldn't happen in manual upload mode)
      addFiles(files);
      for (const fileData of files) {
        try {
          updateFile(fileData.name, {
            ...fileData,
            isUploading: false,
          });
        } catch (err) {
          handleError(err as Error);
          updateFile(fileData.name, {
            isUploading: false,
          });
        }
      }
    }
  };

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept: {
      "image/*": [],
      "video/*": [],
    },
    onDrop: async (acceptedFiles) => {
      console.log("FILE DROPPED", acceptedFiles);

      addFiles(
        acceptedFiles.map((file) => ({
          id: Date.now(),
          name: file.name,
          mine_type: file.type,
          url: URL.createObjectURL(file),
          isUploading: true,
        }))
      );

      for (const file of acceptedFiles) {
        try {
          const filename = file.name;
          const uploadedFile = await uploadFile(file, currentFolder);

          console.log("UPLOADED FILE", uploadedFile);

          updateFile(filename, {
            ...uploadedFile,
            isUploading: false,
          });
        } catch (err) {
          handleError(err as Error);
        }
      }

      // REMOVE TEMP URL
      // URL.revokeObjectURL(file.preview)

      console.log("Files Uploaded");
    },
  });

  return (
    <div
      {...getRootProps({
        className: cx(
          "border-4 rounded-md",
          isDragActive ? "border-blue-400 border-dashed" : "border-transparent"
        ),
      })}
    >
      <div className="p-3">
        <FlexAlign>
          <div className="prose">
            <h2>Files</h2>
          </div>
          <div className="flex items-center">
            <div className="dropdown dropdown-end">
              <div
                tabIndex={0}
                role="button"
                className="btn btn-xs btn-ghost mx-2 px-1"
              >
                <MoreVertical size={18} />
              </div>
              <ul
                tabIndex={0}
                className="dropdown-content menu bg-base-100 rounded-box z-1 w-52 p-2 shadow-sm"
              >
                <li onClick={() => setShowCreateFolder(true)}>
                  <span>
                    <Folder size={14} /> Add Folder
                  </span>
                </li>
                <li onClick={() => setShowManualUpload(true)}>
                  <span>
                    <Settings size={14} /> Manual Upload
                  </span>
                </li>
                <li
                  onClick={() =>
                    setShowFolderSelectModal(
                      !!(selectedCount && selectedCount > 0)
                    )
                  }
                >
                  <span className={cx(selectedCount === 0 && " opacity-40")}>
                    <CornerDownRight size={14} /> Move to Folder
                  </span>
                </li>
                <li
                  onClick={() => {
                    if (selectedCount && selectedCount > 0) {
                      confirmDelete();
                    }
                  }}
                >
                  <span className={cx(selectedCount === 0 && " opacity-40")}>
                    <Trash2 size={14} /> Delete Selected
                  </span>
                </li>
              </ul>
            </div>
            <button className="btn btn-primary btn-sm" onClick={open}>
              <Plus />
              Upload
            </button>
          </div>
        </FlexAlign>

        <input {...getInputProps()} />
        {isEmpty && (
          <div className="mt-6 px-20 py-16 max-w-[380px] border-2 border-dashed rounded-md flex items-center justify-center text-base-content/60 text-center">
            <p>Drag 'n' drop some files here, or click to select files</p>
          </div>
        )}
      </div>
      {children}

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteSelected}
        title="Delete Selected Files"
        message={(() => {
          const unusedCount = selectedFiles.filter((f) => !f.isUsed).length;
          const usedCount = selectedFiles.filter((f) => f.isUsed).length;

          if (usedCount === 0) {
            return `Are you sure you want to delete ${unusedCount} file${
              unusedCount > 1 ? "s" : ""
            }? This action cannot be undone.`;
          } else {
            return `${unusedCount} file${
              unusedCount > 1 ? "s" : ""
            } will be deleted. ${usedCount} file${
              usedCount > 1 ? "s" : ""
            } cannot be deleted because they are used in posts. This action cannot be undone.`;
          }
        })()}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <ManualUploadModal
        isOpen={showManualUpload}
        onClose={() => setShowManualUpload(false)}
        onUpload={handleManualUpload}
        currentFolder={currentFolder}
      />
    </div>
  );
}
